import React from 'react';
import WorkPassField from './WorkPassField';

/**
 * WorkPassFieldGroup — renders one schema section as an accessible <fieldset>.
 *
 * Layout is a responsive 1→2 column grid; fields with colSpan: 2 stretch full
 * width. The group is purely presentational and delegates per-field state to
 * <WorkPassField>.
 *
 * @param {object}   section          schema section { key, title, description, fields }
 * @param {object}   values           map of fieldKey -> value
 * @param {function} onChange         (key, value) => void
 * @param {object}   [errors]         map of fieldKey -> error message
 * @param {Set|object} [prefilledKeys] Set (or map) of keys prefilled from ATS
 * @param {boolean}  [editable]       whether the whole group is interactive
 * @param {boolean}  [lockPrefilled]  render prefilled fields as locked read-only
 * @param {boolean}  [disabled]       disable all controls (e.g. while submitting)
 * @param {'admin'|'employee'} [theme]
 */
const WorkPassFieldGroup = ({
  section,
  values = {},
  onChange,
  errors = {},
  prefilledKeys,
  editable = true,
  lockPrefilled = false,
  disabled = false,
  theme = 'employee',
}) => {
  // Accept a Set, an array of keys (the API shape), or a { key: bool } map.
  const prefilledSet = React.useMemo(() => {
    if (prefilledKeys instanceof Set) return prefilledKeys;
    if (Array.isArray(prefilledKeys)) return new Set(prefilledKeys);
    if (prefilledKeys && typeof prefilledKeys === 'object') {
      return new Set(Object.keys(prefilledKeys).filter((k) => prefilledKeys[k]));
    }
    return new Set();
  }, [prefilledKeys]);
  const isPrefilled = (key) => prefilledSet.has(key);

  return (
    <fieldset className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
      <legend className="px-1 text-base font-semibold text-gray-900">{section.title}</legend>
      {section.description && (
        <p className="mb-4 mt-0.5 text-xs text-gray-500 sm:text-sm">{section.description}</p>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {section.fields.map((field) => {
          const prefilled = isPrefilled(field.key);
          // A prefilled field is locked (read-only) only when the group is
          // editable AND lockPrefilled is on. In a fully read-only group every
          // field is non-editable regardless.
          const locked = editable && lockPrefilled && prefilled;
          return (
            <WorkPassField
              key={field.key}
              field={field}
              value={values[field.key]}
              onChange={onChange}
              error={errors[field.key]}
              editable={editable && !locked}
              prefilled={prefilled}
              locked={locked}
              disabled={disabled}
              theme={theme}
            />
          );
        })}
      </div>
    </fieldset>
  );
};

export default WorkPassFieldGroup;
